import styled, { createGlobalStyle, keyframes } from 'styled-components';

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
`;

const popIn = keyframes`
  from { opacity: 0; transform: scale(0.94); }
  to { opacity: 1; transform: scale(1); }
`;

const aiGlow = keyframes`
  0%, 100% { opacity: 0.5; }
  50% { opacity: 1; }
`;

const dotBounce = keyframes`
  0%, 60%, 100% { transform: translateY(0); opacity: 0.5; }
  30% { transform: translateY(-4px); opacity: 1; }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

export const AiAssistantScrollbars = createGlobalStyle`
  .ai-assistant-scroll {
    scrollbar-width: thin;
    scrollbar-color: rgba(148, 163, 184, 0.35) transparent;
  }
  .ai-assistant-scroll::-webkit-scrollbar { width: 7px; height: 7px; }
  .ai-assistant-scroll::-webkit-scrollbar-track { background: transparent; }
  .ai-assistant-scroll::-webkit-scrollbar-thumb {
    background: rgba(148, 163, 184, 0.3);
    border-radius: 999px;
  }
  .ai-assistant-scroll::-webkit-scrollbar-thumb:hover {
    background: rgba(148, 163, 184, 0.5);
  }
`;

export const Panel = styled.aside<{ $width: number }>`
  --accent: #3b82f6;
  --accent-soft: rgba(59, 130, 246, 0.16);
  --accent-strong: #60a5fa;
  --danger: #f87171;
  --success: #34d399;

  position: fixed;
  top: 0;
  right: 0;
  width: ${(p) => p.$width}px;
  height: 100vh;
  z-index: 1100;
  display: flex;
  flex-direction: column;
  background:
    radial-gradient(1200px 400px at 100% -10%, rgba(59, 130, 246, 0.14), transparent 60%),
    linear-gradient(165deg, #0a0f1d 0%, #111a2e 50%, #0a0f1d 100%);
  border-left: 1px solid rgba(148, 163, 184, 0.14);
  box-shadow: -24px 0 60px rgba(0, 0, 0, 0.45);
  color: #e2e8f0;
  font-family: 'Inter', 'Segoe UI', system-ui, sans-serif;
  transition: width 0.05s linear;

  &[data-mode='edit'] {
    --accent: #8b5cf6;
    --accent-soft: rgba(139, 92, 246, 0.18);
    --accent-strong: #a78bfa;
  }

  * { box-sizing: border-box; }

  .resize-handle {
    position: absolute;
    left: -3px;
    top: 0;
    width: 6px;
    height: 100%;
    cursor: col-resize;
    z-index: 2;
  }
  .resize-handle:hover, .resize-handle:active {
    background: linear-gradient(to right, transparent, var(--accent-soft));
  }

  /* ---------- Header ---------- */
  .header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 14px 14px 18px;
    border-bottom: 1px solid rgba(148, 163, 184, 0.1);
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.03), transparent);
    flex-shrink: 0;
  }

  .brand {
    display: flex;
    align-items: center;
    gap: 10px;
    flex: 1;
    min-width: 0;
  }

  .brand-icon {
    width: 34px;
    height: 34px;
    border-radius: 10px;
    display: grid;
    place-items: center;
    flex-shrink: 0;
    color: white;
    font-size: 14px;
    background: linear-gradient(135deg, var(--accent), #7c3aed 130%);
    box-shadow: 0 4px 14px var(--accent-soft);
  }

  .brand-text { min-width: 0; }

  .brand-text h3 {
    margin: 0;
    font-size: 14.5px;
    font-weight: 700;
    letter-spacing: -0.01em;
    line-height: 1.2;
  }

  .brand-sub {
    display: block;
    font-size: 11px;
    color: #93a1b8;
    margin-top: 1px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .header-actions {
    display: flex;
    gap: 6px;
    flex-shrink: 0;
  }

  .icon-btn {
    border: 1px solid transparent;
    background: rgba(148, 163, 184, 0.1);
    color: #cbd5e1;
    width: 32px;
    height: 32px;
    border-radius: 9px;
    cursor: pointer;
    display: grid;
    place-items: center;
    font-size: 13px;
    transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease, transform 0.1s ease;
  }

  .icon-btn:hover { background: rgba(148, 163, 184, 0.2); color: #fff; }
  .icon-btn:active { transform: scale(0.94); }
  .icon-btn.active {
    background: var(--accent-soft);
    border-color: rgba(148, 163, 184, 0.25);
    color: var(--accent-strong);
  }
  .icon-btn.danger:hover { background: rgba(248, 113, 113, 0.18); color: #fca5a5; }
  .icon-btn:disabled {
    opacity: 0.32;
    cursor: not-allowed;
    pointer-events: none;
  }
  .icon-btn.small {
    width: 28px;
    height: 28px;
    font-size: 11.5px;
  }

  /* ---------- Toolbar ---------- */
  .toolbar-wrap {
    padding: 12px 14px 10px;
    border-bottom: 1px solid rgba(148, 163, 184, 0.08);
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex-shrink: 0;
  }

  .toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    flex-wrap: wrap;
  }

  .toolbar-group {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
  }

  .toolbar-divider {
    width: 1px;
    align-self: stretch;
    background: rgba(148, 163, 184, 0.14);
    margin: 0 2px;
  }

  .tool-btn {
    border: 1px solid rgba(148, 163, 184, 0.16);
    background: rgba(148, 163, 184, 0.06);
    color: #cbd5e1;
    border-radius: 999px;
    padding: 5px 11px;
    font-size: 11.5px;
    font-weight: 500;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    white-space: nowrap;
    transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease, opacity 0.15s ease;
  }

  .tool-btn:hover:not(:disabled) {
    background: rgba(148, 163, 184, 0.14);
    border-color: rgba(148, 163, 184, 0.3);
    color: #fff;
  }

  .tool-btn.active {
    background: var(--accent-soft);
    border-color: rgba(148, 163, 184, 0.3);
    color: var(--accent-strong);
  }

  .tool-btn:disabled { opacity: 0.4; cursor: not-allowed; }

  /* ---------- Body / messages ---------- */
  .body {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }

  .voice-strip {
    margin: 12px 14px 0;
    border: 1px solid rgba(59, 130, 246, 0.24);
    background: rgba(59, 130, 246, 0.09);
    border-radius: 12px;
    padding: 8px 9px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    flex-shrink: 0;
    animation: ${fadeInUp} 0.18s ease both;
  }

  .voice-strip.error {
    border-color: rgba(248, 113, 113, 0.35);
    background: rgba(248, 113, 113, 0.09);
  }

  .voice-state {
    min-width: 0;
    display: inline-flex;
    align-items: center;
    gap: 7px;
    color: #dbeafe;
    font-size: 12px;
    font-weight: 700;
  }

  .voice-state span:last-child {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .voice-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
    background: var(--accent-strong);
    box-shadow: 0 0 0 4px var(--accent-soft);
    animation: ${dotBounce} 1.1s ease-in-out infinite;
  }

  .voice-strip.listening .voice-dot {
    background: var(--success);
    box-shadow: 0 0 0 4px rgba(52, 211, 153, 0.18);
    animation: none;
  }

  .voice-strip.error .voice-dot {
    background: var(--danger);
    box-shadow: 0 0 0 4px rgba(248, 113, 113, 0.18);
    animation: none;
  }

  .voice-actions {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    flex-shrink: 0;
  }

  .voice-icon {
    border: 1px solid rgba(148, 163, 184, 0.18);
    background: rgba(15, 23, 42, 0.48);
    color: #dbeafe;
    width: 30px;
    height: 30px;
    border-radius: 9px;
    cursor: pointer;
    display: grid;
    place-items: center;
    font-size: 11.5px;
    transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease, transform 0.1s ease;
  }

  .voice-icon:hover:not(:disabled) {
    background: rgba(148, 163, 184, 0.14);
    border-color: rgba(96, 165, 250, 0.45);
    color: #fff;
    transform: translateY(-1px);
  }

  .voice-icon.danger {
    color: #fca5a5;
    border-color: rgba(248, 113, 113, 0.28);
    background: rgba(248, 113, 113, 0.08);
  }

  .voice-icon.voice-stop {
    width: auto;
    padding: 0 10px;
    gap: 6px;
    color: #fca5a5;
    border-color: rgba(248, 113, 113, 0.28);
    background: rgba(248, 113, 113, 0.08);
    font-weight: 800;
  }

  .voice-icon:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  .messages {
    flex: 1;
    overflow: auto;
    padding: 16px 14px;
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  .msg-row {
    display: flex;
    gap: 8px;
    max-width: 94%;
    animation: ${fadeInUp} 0.22s ease both;
  }

  .msg-row.user { align-self: flex-end; flex-direction: row-reverse; }
  .msg-row.agent { align-self: flex-start; }

  .msg-avatar {
    width: 24px;
    height: 24px;
    border-radius: 7px;
    flex-shrink: 0;
    display: grid;
    place-items: center;
    font-size: 11px;
    margin-top: 2px;
  }

  .msg-row.agent .msg-avatar {
    color: white;
    background: linear-gradient(135deg, var(--accent), #7c3aed 140%);
  }

  .msg-row.user .msg-avatar {
    color: #cbd5e1;
    background: rgba(148, 163, 184, 0.18);
  }

  .msg-col { min-width: 0; display: flex; flex-direction: column; gap: 3px; }
  .msg-row.user .msg-col { align-items: flex-end; }

  .msg {
    padding: 9px 12px;
    border-radius: 13px;
    font-size: 13px;
    line-height: 1.5;
    white-space: normal;
    word-break: break-word;
    unicode-bidi: plaintext;
  }

  .msg strong { font-weight: 700; color: #f8fafc; }
  .msg a {
    color: #93c5fd;
    font-weight: 650;
    text-decoration: underline;
    text-underline-offset: 2px;
  }
  .msg a:hover { color: #bfdbfe; }

  .msg .md-li {
    display: flex;
    gap: 8px;
    align-items: flex-start;
    margin: 2px 0;
    white-space: normal;
  }

  .msg .md-bullet { flex-shrink: 0; line-height: 1.5; color: var(--accent-strong); }
  .msg .md-li-text { flex: 1; min-width: 0; }

  .msg.rtl { text-align: right; }
  .msg.rtl .md-li { flex-direction: row; }

  .msg-row.user .msg {
    background: linear-gradient(135deg, var(--accent), #6d28d9 160%);
    color: white;
    border-bottom-right-radius: 4px;
  }

  .msg-row.agent .msg {
    background: rgba(148, 163, 184, 0.09);
    border: 1px solid rgba(148, 163, 184, 0.1);
    border-bottom-left-radius: 4px;
  }

  .msg-time {
    font-size: 10px;
    color: #64748b;
    padding: 0 4px;
  }

  .typing-row { align-self: flex-start; }

  .typing-bubble {
    display: flex;
    align-items: center;
    gap: 8px;
    background: rgba(148, 163, 184, 0.09);
    border: 1px solid rgba(148, 163, 184, 0.1);
    border-radius: 13px;
    border-bottom-left-radius: 4px;
    padding: 10px 13px;
    font-size: 12.5px;
    color: #9aa7bd;
  }

  .typing-dots { display: inline-flex; gap: 3px; }
  .typing-dots span {
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: var(--accent-strong);
    animation: ${dotBounce} 1.1s ease-in-out infinite;
  }
  .typing-dots span:nth-child(2) { animation-delay: 0.15s; }
  .typing-dots span:nth-child(3) { animation-delay: 0.3s; }

  /* ---------- Composer ---------- */
  .composer {
    border-top: 1px solid rgba(148, 163, 184, 0.1);
    padding: 12px 14px 14px;
    flex-shrink: 0;
  }

  .composer-shell {
    display: flex;
    align-items: center;
    gap: 2px;
    border-radius: 22px;
    border: 1px solid rgba(148, 163, 184, 0.2);
    background: rgba(15, 23, 42, 0.6);
    padding: 5px 5px 5px 6px;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
  }

  .composer-shell:focus-within {
    border-color: var(--accent);
    box-shadow: 0 0 0 3px var(--accent-soft);
  }

  .composer-mode {
    display: flex;
    gap: 2px;
    flex-shrink: 0;
    padding-right: 6px;
    margin-right: 4px;
    border-right: 1px solid rgba(148, 163, 184, 0.16);
  }

  .composer-mode button {
    border: none;
    background: transparent;
    color: #8b96ac;
    width: 30px;
    height: 30px;
    border-radius: 10px;
    cursor: pointer;
    display: grid;
    place-items: center;
    font-size: 12px;
    transition: background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease, transform 0.1s ease;
  }

  .composer-mode button:hover { color: #e2e8f0; background: rgba(148, 163, 184, 0.14); }
  .composer-mode button:active { transform: scale(0.92); }
  .composer-mode button:disabled {
    opacity: 0.45;
    cursor: not-allowed;
    transform: none;
  }

  .composer-mode button.active {
    background: linear-gradient(135deg, var(--accent), #7c3aed 140%);
    color: white;
    box-shadow: 0 3px 10px var(--accent-soft);
  }

  .composer-shell textarea {
    flex: 1;
    resize: none;
    min-height: 30px;
    max-height: 120px;
    border: none;
    background: transparent;
    color: #f8fafc;
    padding: 6px 6px;
    font-size: 13px;
    line-height: 1.4;
    font-family: inherit;
    outline: none;
  }

  .composer-shell textarea::placeholder { color: #64748b; }

  .composer-send,
  .composer-voice,
  .composer-stop {
    border: none;
    background: linear-gradient(135deg, var(--accent), #7c3aed 140%);
    color: white;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    cursor: pointer;
    display: grid;
    place-items: center;
    flex-shrink: 0;
    font-size: 12.5px;
    box-shadow: 0 4px 12px var(--accent-soft);
    transition: transform 0.12s ease, opacity 0.15s ease, box-shadow 0.15s ease;
  }

  .composer-voice {
    border: 1px solid rgba(148, 163, 184, 0.18);
    background: rgba(148, 163, 184, 0.1);
    color: #cbd5e1;
    box-shadow: none;
  }

  .composer-stop {
    width: auto;
    min-width: 66px;
    padding: 0 11px;
    border-radius: 999px;
    gap: 6px;
    border: 1px solid rgba(248, 113, 113, 0.32);
    background: rgba(248, 113, 113, 0.12);
    color: #fca5a5;
    box-shadow: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-weight: 800;
  }

  .composer-voice.active {
    border-color: rgba(248, 113, 113, 0.32);
    background: rgba(248, 113, 113, 0.12);
    color: #fca5a5;
  }

  .composer-send:hover:not(:disabled),
  .composer-voice:hover:not(:disabled),
  .composer-stop:hover:not(:disabled) {
    transform: translateY(-1px) scale(1.04);
    box-shadow: 0 6px 16px var(--accent-soft);
  }
  .composer-send:active:not(:disabled),
  .composer-voice:active:not(:disabled),
  .composer-stop:active:not(:disabled) { transform: scale(0.92); }

  .composer-send:disabled,
  .composer-voice:disabled,
  .composer-stop:disabled {
    opacity: 0.35;
    cursor: not-allowed;
    box-shadow: none;
    background: rgba(148, 163, 184, 0.25);
  }

  /* ---------- Full-height views (settings / history) ---------- */
  .view-panel {
    flex: 1;
    min-height: 0;
    overflow: auto;
    padding: 16px 14px;
    display: flex;
    flex-direction: column;
    animation: ${fadeInUp} 0.18s ease both;
  }

  .view-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-bottom: 14px;
  }

  .btn-back {
    border: 1px solid rgba(148, 163, 184, 0.18);
    background: rgba(148, 163, 184, 0.07);
    color: #9aa7bd;
    border-radius: 999px;
    padding: 5px 12px 5px 9px;
    font-size: 11.5px;
    font-weight: 500;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    flex-shrink: 0;
    transition: background 0.15s ease, color 0.15s ease;
  }
  .btn-back:hover { background: rgba(148, 163, 184, 0.16); color: #e2e8f0; }

  /* ---------- Sections (pending / restore / persist) ---------- */
  .section {
    padding: 14px;
    border-top: 1px solid rgba(148, 163, 184, 0.1);
    max-height: 44%;
    overflow: auto;
    animation: ${fadeInUp} 0.18s ease both;
    flex-shrink: 0;
  }

  .section-title {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0 0 10px;
    font-size: 12.5px;
    font-weight: 700;
    letter-spacing: 0.01em;
    text-transform: uppercase;
    color: #a8b3c6;
  }

  .section-title .ico {
    width: 20px;
    height: 20px;
    border-radius: 6px;
    display: grid;
    place-items: center;
    background: var(--accent-soft);
    color: var(--accent-strong);
    font-size: 10px;
  }

  .section h4 { margin: 0 0 8px; font-size: 13px; }
  .section p.hint { font-size: 11.5px; color: #8b96ac; margin: 0 0 10px; line-height: 1.5; }

  .row {
    display: flex;
    gap: 8px;
    margin-bottom: 8px;
    align-items: center;
  }

  .row label {
    width: 84px;
    font-size: 11.5px;
    color: #94a3b8;
    flex-shrink: 0;
  }

  .row select, .row input {
    flex: 1;
    border-radius: 9px;
    border: 1px solid rgba(148, 163, 184, 0.22);
    background: rgba(15, 23, 42, 0.6);
    color: #f8fafc;
    padding: 7px 9px;
    font-size: 12px;
    outline: none;
    transition: border-color 0.15s ease;
  }

  .row select:focus, .row input:focus { border-color: var(--accent); }

  .row select:disabled, .row input:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  .actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 10px;
  }

  .actions button {
    border: none;
    border-radius: 9px;
    padding: 8px 13px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    transition: filter 0.15s ease, transform 0.1s ease;
  }
  .actions button:hover { filter: brightness(1.08); }
  .actions button:active { transform: scale(0.97); }
  .actions button:disabled {
    opacity: 0.45;
    cursor: not-allowed;
    filter: none;
  }

  .btn-apply { background: linear-gradient(135deg, #22c55e, #16a34a); color: #04220f; }
  .btn-cancel { background: rgba(148, 163, 184, 0.16); color: #e2e8f0; }
  .btn-persist { background: linear-gradient(135deg, #3b82f6, #2563eb); color: white; }
  .btn-danger { background: linear-gradient(135deg, #ef4444, #dc2626); color: white; }
  .btn-muted { background: rgba(148, 163, 184, 0.12); color: #e2e8f0; }

  /* ---------- Pending change card ---------- */
  .pending-card {
    border: 1px solid rgba(245, 158, 11, 0.3);
    background: linear-gradient(160deg, rgba(245, 158, 11, 0.08), rgba(148, 163, 184, 0.04));
    border-radius: 12px;
    padding: 12px 13px;
  }

  .pending-summary {
    font-size: 12.5px;
    margin: 0 0 8px;
    color: #f1f5f9;
    line-height: 1.5;
  }

  .warn-card {
    border: 1px solid rgba(248, 113, 113, 0.3);
    background: rgba(248, 113, 113, 0.08);
    border-radius: 10px;
    padding: 8px 10px;
    font-size: 11.5px;
    color: #fca5a5;
    margin-top: 8px;
  }

  /* ---------- Gate ---------- */
  .gate {
    flex: 1;
    display: grid;
    place-items: center;
    padding: 24px;
    text-align: center;
  }

  .gate-card {
    width: 100%;
    max-width: 280px;
    background: rgba(15, 23, 42, 0.7);
    border: 1px solid rgba(148, 163, 184, 0.18);
    border-radius: 16px;
    padding: 24px 20px;
    animation: ${popIn} 0.2s ease both;
  }

  .gate-icon {
    width: 46px;
    height: 46px;
    margin: 0 auto 14px;
    border-radius: 13px;
    display: grid;
    place-items: center;
    color: white;
    font-size: 18px;
    background: linear-gradient(135deg, var(--accent), #7c3aed 140%);
    box-shadow: 0 8px 22px var(--accent-soft);
  }

  .gate-card h4 { margin: 0 0 8px; font-size: 14.5px; }
  .gate-card p { margin: 0 0 16px; color: #94a3b8; font-size: 12px; line-height: 1.5; }
  .gate-card input {
    width: 100%;
    text-align: center;
    font-size: 15px;
    letter-spacing: 3px;
    border-radius: 11px;
    border: 1px solid rgba(148, 163, 184, 0.28);
    background: #020617;
    color: white;
    padding: 11px;
    margin-bottom: 10px;
    box-sizing: border-box;
    outline: none;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
  }
  .gate-card input:focus { border-color: var(--accent); box-shadow: 0 0 0 3px var(--accent-soft); }
  .gate-error {
    color: #fca5a5;
    font-size: 11.5px;
    margin-bottom: 8px;
    background: rgba(248, 113, 113, 0.1);
    border-radius: 8px;
    padding: 6px 8px;
  }

  /* ---------- History items ---------- */
  .history-tabs {
    display: flex;
    gap: 6px;
    padding: 0 0 12px;
    border: none;
    flex-wrap: wrap;
  }

  .history-item {
    border: 1px solid rgba(148, 163, 184, 0.14);
    background: rgba(148, 163, 184, 0.04);
    border-radius: 11px;
    padding: 9px 11px;
    margin-bottom: 8px;
    cursor: pointer;
    transition: border-color 0.15s ease, background 0.15s ease, transform 0.1s ease;
  }
  .history-item:hover { border-color: var(--accent); background: var(--accent-soft); transform: translateX(-2px); }
  .history-item.active {
    border-color: var(--accent);
    background: var(--accent-soft);
    box-shadow: 0 0 0 1px var(--accent) inset;
  }
  .history-item > div:first-child { font-size: 12.5px; font-weight: 600; color: #f1f5f9; }
  .history-item small { color: #8b96ac; font-size: 11px; }

  .empty-hint {
    font-size: 11.5px;
    color: #8b96ac;
    background: rgba(148, 163, 184, 0.06);
    border: 1px dashed rgba(148, 163, 184, 0.2);
    border-radius: 10px;
    padding: 12px;
    text-align: center;
  }

  .usage-tiles {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
    margin-bottom: 10px;
  }

  .usage-tile {
    border: 1px solid rgba(148, 163, 184, 0.14);
    background: rgba(148, 163, 184, 0.05);
    border-radius: 11px;
    padding: 10px 11px;
  }

  .usage-tile .label {
    font-size: 10.5px;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: #8b96ac;
    margin-bottom: 4px;
  }

  .usage-tile .value { font-size: 15px; font-weight: 700; color: #f1f5f9; }
  .usage-tile .sub { font-size: 10.5px; color: #7d8aa3; margin-top: 2px; }

  .diff-list {
    font-size: 12px;
    color: #cbd5e1;
    margin: 0;
    padding-left: 4px;
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 5px;
  }

  .diff-list li {
    display: flex;
    align-items: center;
    gap: 7px;
  }

  .diff-list li::before {
    content: '';
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: var(--accent-strong);
    flex-shrink: 0;
  }

  .inline-status {
    margin-top: 10px;
    font-size: 11.5px;
    color: #9aa7bd;
    background: rgba(148, 163, 184, 0.08);
    border-radius: 8px;
    padding: 7px 10px;
  }

  .spinner {
    display: inline-block;
    width: 11px;
    height: 11px;
    border-radius: 50%;
    border: 2px solid rgba(148, 163, 184, 0.3);
    border-top-color: var(--accent-strong);
    animation: ${spin} 0.7s linear infinite;
  }

  /* ---------- Widget theme overrides (provided HTML/CSS snippet) ---------- */
  background:
    radial-gradient(900px 600px at 15% -10%, rgba(129, 114, 255, 0.16) 0%, transparent 55%),
    radial-gradient(800px 600px at 100% 20%, rgba(84, 224, 208, 0.10) 0%, transparent 50%),
    linear-gradient(180deg, #160F3A 0%, #100C2C 100%);
  border-left: 1px solid rgba(255, 255, 255, 0.14);
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.06) inset,
    0 30px 60px -20px rgba(0, 0, 0, 0.6),
    0 0 0 1px rgba(129, 114, 255, 0.06);
  color: #F4F2FF;
  border-top-left-radius: 22px;
  border-bottom-left-radius: 22px;
  overflow: hidden;
  font-family: 'Inter', 'Segoe UI', system-ui, sans-serif;

  .header {
    padding: 16px 16px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    background: transparent;
  }

  .brand-icon {
    width: 38px;
    height: 38px;
    border-radius: 11px;
    background: linear-gradient(150deg, #8172FF, #4F3FE0 75%);
    box-shadow:
      0 1px 0 rgba(255, 255, 255, 0.3) inset,
      0 -3px 6px rgba(0, 0, 0, 0.22) inset,
      0 10px 20px -8px rgba(79, 63, 224, 0.6);
  }
  .brand-icon svg {
    width: 18px;
    height: 18px;
  }

  .brand-text h3 {
    font-family: 'Space Grotesk', Inter, system-ui, sans-serif;
    font-weight: 700;
    font-size: 14.5px;
    line-height: 1.2;
  }

  .brand-sub {
    font-size: 11px;
    color: #6E6890;
    margin-top: 1px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .icon-btn {
    border: 1px solid rgba(255, 255, 255, 0.14);
    background: rgba(255, 255, 255, 0.03);
    color: #A8A3C6;
    transition: 0.16s;
  }
  .icon-btn:hover {
    color: #fff;
    border-color: rgba(129, 114, 255, 0.4);
    background: rgba(255, 255, 255, 0.06);
  }
  .icon-btn.active {
    background: linear-gradient(150deg, #8172FF, #4F3FE0);
    color: #fff;
    border-color: transparent;
    box-shadow: 0 6px 14px -6px rgba(79, 63, 224, 0.6);
  }

  .toolbar-wrap {
    padding: 12px 16px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  }

  .tool-btn {
    border: 1px solid rgba(255, 255, 255, 0.14);
    background: rgba(255, 255, 255, 0.04);
    color: #A8A3C6;
    border-radius: 10px;
    padding: 8px 13px;
    font-size: 12px;
    font-weight: 600;
    transition: 0.16s;
  }
  .tool-btn:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.08);
    color: #F4F2FF;
    border-color: rgba(129, 114, 255, 0.35);
  }
  .tool-btn.active {
    background: linear-gradient(150deg, #8172FF, #4F3FE0);
    color: #fff;
    border-color: transparent;
    box-shadow: 0 6px 14px -6px rgba(79, 63, 224, 0.55);
  }

  .messages {
    padding: 16px;
    gap: 14px;
  }

  .msg-row.user {
    align-self: flex-end;
    flex-direction: row-reverse;
  }
  .msg-row.agent {
    align-self: flex-start;
  }

  .msg-avatar {
    width: 28px;
    height: 28px;
    border-radius: 9px;
    background: linear-gradient(150deg, #8172FF, #4F3FE0);
    box-shadow:
      0 1px 0 rgba(255, 255, 255, 0.3) inset,
      0 6px 12px -5px rgba(79, 63, 224, 0.55);
    flex-shrink: 0;
    display: grid;
    place-items: center;
    color: #fff;
  }

  .msg {
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.14);
    border-top-left-radius: 4px;
    padding: 12px 14px;
    font-size: 13px;
    line-height: 1.6;
    color: #A8A3C6;
    word-break: break-word;
  }
  .msg strong {
    color: #F4F2FF;
    font-weight: 700;
  }

  .composer {
    border-top: 1px solid rgba(255, 255, 255, 0.08);
    padding: 12px 14px 16px;
  }

  .composer-shell {
    border-radius: 14px;
    border: 1px solid rgba(255, 255, 255, 0.14);
    background: rgba(255, 255, 255, 0.04);
    padding: 6px;
    transition: 0.18s;
  }
  .composer-shell:focus-within {
    border-color: rgba(129, 114, 255, 0.45);
    box-shadow: 0 0 0 3px rgba(129, 114, 255, 0.14);
  }

  .composer-mode {
    margin-right: 0;
    padding-right: 0;
    border-right: none;
  }

  .composer-mode button {
    width: 32px;
    height: 32px;
    border-radius: 10px;
    border: none;
    background: rgba(255, 255, 255, 0.06);
    color: #6E6890;
    display: grid;
    place-items: center;
    transition: 0.16s;
  }
  .composer-mode button.active {
    background: linear-gradient(150deg, #8172FF, #4F3FE0);
    color: #fff;
    box-shadow: 0 1px 0 rgba(255, 255, 255, 0.3) inset, 0 6px 12px -5px rgba(79, 63, 224, 0.55);
  }

  .composer-shell textarea {
    background: transparent;
    border: none;
    color: #F4F2FF;
    padding: 6px 2px;
    font-size: 13px;
  }
  .composer-shell textarea::placeholder {
    color: #6E6890;
  }

  .composer-send {
    border: none;
    background: rgba(255, 255, 255, 0.06);
    color: #6E6890;
    width: 32px;
    height: 32px;
    border-radius: 10px;
    box-shadow: none;
  }
  .composer-send:hover:not(:disabled) {
    background: linear-gradient(150deg, #8172FF, #4F3FE0);
    color: #fff;
  }
  .composer-send:active:not(:disabled) {
    transform: scale(0.92);
  }
  .composer-send:disabled {
    opacity: 0.35;
    cursor: not-allowed;
    background: rgba(255, 255, 255, 0.12);
  }

  .gate {
    position: absolute;
    inset: 0;
    z-index: 10;
    background: linear-gradient(180deg, rgba(16, 12, 44, 0.72), rgba(7, 6, 19, 0.9));
    backdrop-filter: blur(6px);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
  }
  .gate-card {
    width: 100%;
    max-width: 290px;
    text-align: center;
    background: rgba(255, 255, 255, 0.03);
    border: 1px solid rgba(255, 255, 255, 0.14);
    border-radius: 20px;
    padding: 32px 26px;
    box-shadow: 0 30px 60px -24px rgba(0, 0, 0, 0.7);
    animation: ${popIn} 0.2s ease both;
  }
  .gate-icon {
    width: 56px;
    height: 56px;
    border-radius: 16px;
    margin: 0 auto 18px;
    background: linear-gradient(150deg, #8172FF, #4F3FE0);
    box-shadow:
      0 1px 0 rgba(255, 255, 255, 0.3) inset,
      0 -4px 8px rgba(0, 0, 0, 0.25) inset,
      0 16px 30px -10px rgba(79, 63, 224, 0.6);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .gate-icon svg {
    width: 24px;
    height: 24px;
  }
  .gate-card h4 {
    font-family: 'Space Grotesk', sans-serif;
    font-weight: 700;
    font-size: 17px;
    margin-bottom: 9px;
  }
  .gate-card p {
    font-size: 12.5px;
    color: #A8A3C6;
    line-height: 1.6;
    margin-bottom: 22px;
  }
  .gate-card input {
    width: 100%;
    background: rgba(0, 0, 0, 0.25);
    border: 1.5px solid rgba(255, 255, 255, 0.14);
    border-radius: 11px;
    padding: 13px 15px;
    color: #F4F2FF;
    font-family: inherit;
    font-size: 13px;
    text-align: center;
    letter-spacing: 0.1em;
    outline: none;
    margin-bottom: 16px;
    box-sizing: border-box;
  }
  .gate-card input::placeholder {
    color: #6E6890;
    letter-spacing: 0.1em;
  }
  .gate-error {
    color: #E2604A;
    font-size: 11.5px;
    margin-bottom: 16px;
    background: rgba(226, 96, 74, 0.08);
    border-radius: 11px;
    padding: 7px 10px;
  }

  .btn-persist {
    width: 100%;
    padding: 12px;
    border: none;
    border-radius: 11px;
    cursor: pointer;
    background: linear-gradient(150deg, #8172FF, #4F3FE0);
    color: #fff;
    font-family: inherit;
    font-weight: 700;
    font-size: 13.5px;
    box-shadow:
      0 1px 0 rgba(255, 255, 255, 0.3) inset,
      0 12px 24px -10px rgba(79, 63, 224, 0.6);
    transition: 0.16s;
  }
  .btn-persist:hover {
    transform: translateY(-1px);
  }

  .view-header {
    padding: 4px 16px 12px;
    margin-bottom: 14px;
  }
  .section-title {
    font-family: 'IBM Plex Mono', ui-monospace, monospace;
    font-size: 11px;
    letter-spacing: 0.07em;
    text-transform: uppercase;
    color: #A8A3C6;
  }
  .btn-back {
    border: 1px solid rgba(255, 255, 255, 0.14);
    background: rgba(255, 255, 255, 0.04);
    color: #A8A3C6;
    border-radius: 999px;
    padding: 5px 11px;
    font-size: 11.5px;
    font-weight: 600;
    gap: 5px;
  }
`;

export const Fab = styled.button`
  position: absolute;
  right: 26px;
  bottom: 22px;
  z-index: 1090;
  isolation: isolate;
  height: 52px;
  border: none;
  border-radius: 999px;
  padding: 0 24px 0 18px;
  display: flex;
  gap: 10px;
  align-items: center;
  color: white;
  font-size: 14px;
  font-weight: 700;
  background: linear-gradient(140deg, #8172FF 0%, #4F3FE0 60%, #3B2FBF 100%);
  cursor: pointer;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.3) inset,
    0 -4px 8px rgba(0, 0, 0, 0.16) inset,
    0 20px 36px -12px rgba(79, 63, 224, 0.6);
  transition: transform 0.18s ease, box-shadow 0.18s ease;

  &::before {
    content: '';
    position: absolute;
    inset: -6px;
    border-radius: 999px;
    background: radial-gradient(circle, rgba(129, 114, 255, 0.5), transparent 70%);
    filter: blur(10px);
    z-index: -1;
    animation: ${aiGlow} 2.6s ease-in-out infinite;
    pointer-events: none;
  }

  &:hover { transform: translateY(-2px); }
  &:active { transform: scale(0.96); }

  .fab-icon {
    width: 22px;
    height: 22px;
    display: grid;
    place-items: center;
  }
`;
